import type { MailboxInfo } from "owa-client-types";
import { getFolderTable, getMailboxInfoForFolderId } from "owa-folders";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { isUnifiedInboxFolder } from "owa-unified-inbox-data-utils/lib/isUnifiedInboxFolder";

const cachedMailboxInfo: Map<string, MailboxInfo> = new Map();

/**
 * Utility which returns user mailbox info as per folder id
 * @param folderId folder for which to fetch mailbox info
 */
export default function fetchUserMailboxInfoFromFolderId(folderId: string | undefined | null): MailboxInfo {
    // Return request options as default mailbox info if folderId is not present or hasn't been added to the satchel store yet
    if (!folderId || !getFolderTable()?.has(folderId)) {
        return getGlobalSettingsAccountMailboxInfo();
    }
    
    // Archive folders should not use cached mailboxInfo because different archive
    // folders can reside in different auxiliary mailboxes (identified by replicaList).
    // The replicaList may not be available when the folder is first loaded into the
    // table, and the cache would then persist the wrong auxiliaryMailboxGuid.
    const folder = getFolderTable()?.get(folderId);
    const isArchiveFolder = folder?.mailboxInfo?.type === "ArchiveMailbox";
    
    // Save recomputation by storing the request options in a map per folder
    // Unified inbox folders and archive folders are not cached since they can have
    // different mailboxInfos depending on when data becomes available.
    if (!cachedMailboxInfo.has(folderId) || isUnifiedInboxFolder(folderId) || isArchiveFolder) {
        const mailboxInfo = getMailboxInfoForFolderId(folderId, true /* routeToAuxIfAuxArchive */);
        cachedMailboxInfo.set(folderId, mailboxInfo);
    }
    
    return cachedMailboxInfo.get(folderId) ?? getGlobalSettingsAccountMailboxInfo();
}
